import { MapViewer } from '@3ddv/dvm-internal';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { ModalsService } from '@core/modals/modals.service';
import { Seat } from '@core/models';
import {
  AvailabilityService,
  CartService,
  ConfigurationService,
  DvmService,
  TutorialService,
  UtilitiesService,
} from '@core/services';
import { ANALYTICS_EVENTS, AnalyticsService } from '@core/services/analytics';
import { AdobeDataLayerService } from '@core/services/adobe-data-layer-service.service';
import { buildCartItems } from '@features/pages/select-seat/_utils/cart-items.builder';
import { UpgradesService } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.service';
import { Subscription } from 'rxjs';
import { IsolatedSeatsService } from './isolated-seats.service';

export type CombinedIds = {
  original_id: string;
  id: string;
};

@Injectable({
  providedIn: 'root',
})
export class SeatManagementService implements OnDestroy {
  private availabilityService: AvailabilityService =
    inject(AvailabilityService);
  private cartService: CartService = inject(CartService);
  private configurationService: ConfigurationService =
    inject(ConfigurationService);
  private dvmService: DvmService = inject(DvmService);
  private isolatedSeatsService: IsolatedSeatsService =
    inject(IsolatedSeatsService);
  private tutorialService: TutorialService = inject(TutorialService);
  private utilitiesService: UtilitiesService = inject(UtilitiesService);
  private modalsService: ModalsService = inject(ModalsService);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private adobeDataLayerService: AdobeDataLayerService = inject(
    AdobeDataLayerService
  );
  private upgradesService: UpgradesService = inject(UpgradesService);
  needADA: boolean = false;
  subscriptions: Subscription[] = [];
  viewer: MapViewer;
  private cartSelectedSectionIds: string[] = [];

  constructor() {
    const tutorial$ = this.tutorialService.tutorial$.subscribe(
      tutorialConfig => {
        if (tutorialConfig.activeStepCommand === 'setSeatsInCart') {
          const selectedSeatNodes =
            this.tutorialService.getTutorialSelectedSeatNodes();

          this.selectAvailableSeats(
            selectedSeatNodes.map(node => ({
              id: node.id,
              original_id: node.original_id,
            })),
            true
          );
        }

        if (tutorialConfig.isTutorialEnded) {
          this.unselectAllSelectedSeats(true);
        }
      }
    );
    this.dvmService.viewerSubscriptions.push(tutorial$);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  selectAvailableSeats(nodeIds: CombinedIds[], isSynthetic: boolean = false) {
    if (isSynthetic) {
      this.upgradesService.beginSyntheticCartEmission();
    }
    try {
      if (nodeIds.length > 1) {
        this.dvmService.viewerService.unselectAll();
        this.cartService.selectedSeats = {};
        this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
      }

      let ids = nodeIds.map(node => node.id);
      this.dvmService.viewerService.select(ids);

      this.selectSeatsForCart(nodeIds, isSynthetic);
    } finally {
      if (isSynthetic) {
        this.upgradesService.endSyntheticCartEmission();
      }
    }
  }

  unselectSelectedSeats(
    nodeIds: { id: string; original_id?: string; originalId?: string }[]
  ) {
    let success = this.dvmService.viewerService.unselect(nodeIds);

    const removedSeats: Seat[] = [];
    for (const nodeId of nodeIds) {
      const removedSeat = this.cartService.selectedSeats[nodeId.id];
      if (removedSeat) {
        removedSeats.push(removedSeat);
      }
      let isDeleted = delete this.cartService.selectedSeats[nodeId.id];
      this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    }

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    this.updateCartSeatsBySection();

    this.trackRemoveFromCart(removedSeats);
  }

  unselectAllSelectedSeats(isSynthetic: boolean = false) {
    const removedSeats = Object.values(this.cartService.selectedSeats);
    let success = this.dvmService.viewerService.unselectAll();

    this.cartService.selectedSeats = {};
    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    this.updateCartSeatsBySection();

    this.cartService.resetCart();

    if (!this.dvmService.isTopView()) {
      const subscription = this.dvmService.openSectionMap().subscribe();
      this.subscriptions.push(subscription);
    }

    if (!isSynthetic) {
      this.trackRemoveFromCart(removedSeats);
    }
  }

  unselectAllSelectedSeatsWithoutLoadingMap() {
    const removedSeats = Object.values(this.cartService.selectedSeats);
    const selectedSeats = this.dvmService.viewerService.getNodes({
      type: ['seat'],
      state: 'selected',
    });
    this.dvmService.viewerService.unselect(selectedSeats);
    this.cartService.selectedSeats = {};
    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    this.updateCartSeatsBySection();

    this.trackRemoveFromCart(removedSeats);
  }

  selectSeatsForCart(
    nodeIds: CombinedIds[] | string[],
    isSynthetic: boolean = false
  ) {
    const unavailableSeatIds: string[] = [];
    const addedSeats: Seat[] = [];

    for (const node of nodeIds) {
      const newSeat = this.utilitiesService.splittedSeatInfoFromId(
        node
      ) as Seat;
      const id = typeof node === 'string' ? node : node.id;

      const seatInfo = this.availabilityService.getSeatByIdFromAvailability(id);

      if (!seatInfo) {
        console.error(
          `Seat ${id} has no availability entry - skipping cart selection`,
          {
            isTutorialOpen: this.tutorialService.isOpen(),
            derivedSectionId: id.split('-')[0],
            loadedSectionBuckets: Object.keys(
              this.availabilityService.seatsAvailability
            ),
          }
        );
        unavailableSeatIds.push(id);
        continue;
      }

      newSeat.price = seatInfo?.price?.[0]?.value;
      newSeat.totalFee = seatInfo?.price?.[0]?.totalFee;
      newSeat.totalPriceWithFees = seatInfo?.price?.[0]?.totalPriceWithFees;
      this.cartService.selectedSeats[newSeat.id] = newSeat;
      addedSeats.push(newSeat);
      this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    }

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    this.updateCartSeatsBySection();

    if (unavailableSeatIds.length > 0) {
      this.dvmService.viewerService.unselect(
        unavailableSeatIds.map(id => ({ id }))
      );

      this.modalsService.openModal({
        title: 'Seat(s) no longer available',
        content: `The following seat(s) are no longer available and were not added to your selection: ${unavailableSeatIds.join(', ')}.`,
        acceptBtnName: 'CLOSE',
      });

      this.analyticsService.trackError({
        eventName: ANALYTICS_EVENTS.SEAT_SELECTION_ERROR,
        errorType: 'seats_unavailable',
        errorMessage: `Seat(s) no longer available: ${unavailableSeatIds.join(', ')}`,
        gameId: this.adobeDataLayerService.getEventId() || undefined,
      });
    }

    if (!isSynthetic) {
      this.trackAddToCart(addedSeats);
    }
  }

  private trackAddToCart(addedSeats: Seat[]): void {
    if (addedSeats.length === 0) {
      return;
    }

    const value = addedSeats.reduce(
      (subtotal, seat) => subtotal + (seat.price ?? 0),
      0
    );
    const seatCount = Object.keys(this.cartService.selectedSeats).length;

    this.analyticsService.trackAddToCart({
      currency: 'USD',
      value,
      items: this.buildAnalyticsItems(addedSeats),
      gameId: this.adobeDataLayerService.getEventId() || undefined,
      seatCount,
    });
  }

  private trackRemoveFromCart(removedSeats: Seat[]): void {
    if (removedSeats.length === 0) {
      return;
    }

    const value = removedSeats.reduce(
      (subtotal, seat) => subtotal + (seat.price ?? 0),
      0
    );
    const seatCount = Object.keys(this.cartService.selectedSeats).length;

    this.analyticsService.trackRemoveFromCart({
      currency: 'USD',
      value,
      items: this.buildAnalyticsItems(removedSeats),
      gameId: this.adobeDataLayerService.getEventId() || undefined,
      seatCount,
    });
  }

  private buildAnalyticsItems(seats: Seat[]) {
    const gameId = String(this.adobeDataLayerService.getEventId() ?? '');
    const matchup = this.adobeDataLayerService.getMatchupFull() ?? '';
    return buildCartItems(seats, gameId, matchup);
  }

  updateCartSeatsBySection() {
    this.cartService.selectedSeatsBySection =
      this.utilitiesService.distributeSeatsBySection(
        Object.values(this.cartService.selectedSeats)
      );

    this.cartService.sortedSelectedSeatsBySectionArray =
      this.utilitiesService.sortSectionsBySeatsQuantity(
        this.cartService.selectedSeatsBySection
      );
    this.cartService.sortedSelectedSeatsBySectionArray$.next(
      this.cartService.sortedSelectedSeatsBySectionArray
    );

    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    this.isolatedSeatsService.isolatedSeatsActive = false;

    this.isolatedSeatsService.lastIsolatedSeats = null;
    this.dvmService.viewerService.removeNodesFromGroup(
      this.dvmService.viewerService.getNodesByGroups('seat', 'pending'),
      'pending'
    );

    this.updateSelectedSectionsOnTopview();
  }

  updateSelectedSectionsOnTopview() {
    if (!this.dvmService.isTopView()) {
      return;
    }

    const selectedSeatsSectionIds = Object.values(
      this.cartService.selectedSeatsBySection
    );
    const nodeList = selectedSeatsSectionIds.map(item => item.id);

    const staleSelected = this.cartSelectedSectionIds.filter(
      sectionId => !nodeList.includes(sectionId)
    );
    if (staleSelected.length) {
      this.dvmService.viewerService.unselect(staleSelected);
    }
    if (nodeList.length) {
      this.dvmService.viewerService.select(nodeList);
    }
    this.cartSelectedSectionIds = nodeList;

    const previouslyHighlightedNodes =
      this.dvmService.viewerService.getNodesByGroups('section', 'group1');
    this.dvmService.viewerService.removeNodesFromGroup(
      previouslyHighlightedNodes,
      'group1'
    );
    if (nodeList.length) {
      this.dvmService.viewerService.addNodesToGroup(nodeList, 'group1');
    }

    nodeList.forEach(node => {
      this.dvmService.viewerService.setAvailable('section', node);
    });

    const dvmData = this.configurationService.configuration.dvmData;

    if (dvmData.miniMapEnabled && dvmData.miniMapId) {
      this.dvmService.minimapService.unselectAll();
      if (selectedSeatsSectionIds.length) {
        this.dvmService.minimapService.select(selectedSeatsSectionIds);
      }
    }
  }
}
